import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { apiError } from '../lib/api';
import { ShieldCheck, BarChart3 } from 'lucide-react';

export default function Login() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setLoading(true);
    try { await signIn(email.trim(), password); navigate('/dashboard'); }
    catch (e2) { setErr(apiError(e2, 'Credenziali non valide')); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md p-8 shadow-md">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-lg bg-black text-white grid place-items-center font-bold tracking-tight">FH</div>
            <div className="leading-tight">
              <div className="font-bold text-black tracking-tight">HOLDING.FIER</div>
              <div className="text-xs text-muted-foreground">Piano Industriale</div>
            </div>
          </div>
          <h1 className="font-display text-2xl font-semibold text-foreground">Accedi</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Inserisci le tue credenziali aziendali.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" autoComplete="username" placeholder="nome@fier.holding" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            {err && <Alert variant="destructive" data-testid="login-error-alert"><AlertDescription>{err}</AlertDescription></Alert>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">{loading ? 'Accesso in corso…' : 'Accedi'}</Button>
          </form>
        </Card>
      </div>
      <div className="hidden lg:flex flex-col justify-center p-12 relative overflow-hidden bg-[hsl(var(--sidebar))] border-l">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at top right, rgba(100,116,139,0.14), transparent 55%)' }} />
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-semibold text-foreground leading-tight">Il tuo Piano Industriale, sempre sotto controllo.</h2>
          <p className="text-muted-foreground mt-4">KPI, margini e controllo di gestione basato sul bilancio riclassificato — in un'unica console aziendale.</p>
          <div className="mt-8 space-y-3">
            <div className="flex items-center gap-3 text-sm"><div className="h-9 w-9 rounded-lg bg-card border grid place-items-center"><BarChart3 className="h-4 w-4 text-primary" /></div><span>KPI economici e finanziari in tempo reale</span></div>
            <div className="flex items-center gap-3 text-sm"><div className="h-9 w-9 rounded-lg bg-card border grid place-items-center"><ShieldCheck className="h-4 w-4 text-primary" /></div><span>Accessi distinti: Direzione e Controllo di Gestione</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
